import React, { useState, useEffect } from "react";
import { Container } from "reactstrap";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../../../components/SearchBar";
import { formatDateTime } from "../../../../constants/Helper";
import { FaPlus } from "react-icons/fa";
import { ExclusiveDealsAPI } from "../../../../api/exclusiveDeals";
import SPINNER from "../../../../components/Spinner";
import { TableCol, TableRow } from "../../../../components/TableElements";
import { FaEdit, FaTrash } from "react-icons/fa";

const ExclusiveList = () => {
  document.title = "Casino24 - Exclusive Deals List";
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 30;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await ExclusiveDealsAPI.getAll();
        if (res.data) {
          setData(res.data);
        }
      } catch (error) {
        alert("Something went wrong. Try Again!");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const filteredRecords = data.filter((record: any) => {
    return (
      searchQuery === "" ||
      record.contentName.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const handleDelete = async (id: number) => {
    setLoading(true);
    if (window.confirm("Are you sure you want to delete this deal?")) {
      try {
        const res = await ExclusiveDealsAPI.deleteDeal(id);
        if (res.success) {
          setData((prevData) => prevData.filter((record) => record.id !== id));
        } else {
          alert("Something went wrong. Try Again");
        }
      } catch (error) {
        alert("Something went wrong. Try Again");
      }
    }
    setLoading(false);
  };

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord
  );
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);

  return (
    <Container
      fluid
      className="p-0 m-0 flex flex-col text-black w-full h-screen bg-[#eeeeee] overflow-scroll relative"
    >
      {/* Header */}
      <section className="p-4 text-[3xl] w-full h-fit bg-white font-bold">
        <h2>Exclusive Deals List</h2>
      </section>

      {/* Data Section */}
      <section className="flex-1 p-4 justify-center items-center">
        {/* Search and Filters */}
        <section className="bg-white mb-3 w-full overflow-x-scroll p-2">
          <h2 className="border-b pb-2 pl-3 mb-3 text-gray-600 w-full text-xl">
            Filter
          </h2>

          <div className="flex flex-col md:flex-row items-center md:items-center justify-center md:justify-between">
            <SearchBar
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
            />
            {/* Status */}

            <button
              className={`bg-green-600 text-white text-sm p-2 px-4 rounded-md flex items-center m-2 ${
                data.length >= 4 && "cursor-not-allowed !bg-gray-600"
              }`}
              onClick={() => navigate("/vips/add-exclusive-deal")}
              disabled={data.length >= 4 ? true : false}
            >
              <FaPlus className="mr-2" /> Add Deal
            </button>
          </div>
        </section>
        {/* Table */}
        <section className="bg-white w-full overflow-x-scroll">
          <table className="w-full border text-[0.8rem]">
            <thead>
              <tr>
                <TableCol value="Registration Time" />
                <TableCol value="Content Name" />
                <TableCol value="URL" />
                <TableCol value="Logo" />
                <TableCol value="Status" />
                <TableCol value="Actions" />
              </tr>
            </thead>
            <tbody>
              {currentRecords.map((record, index) => (
                <tr key={index}>
                  <TableRow value={formatDateTime(record.registrationTime)} />
                  <TableRow value={record.contentName} />
                  <TableRow>
                    <a
                      href={record.website}
                      className="text-blue-500"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {record.refURL}
                    </a>
                  </TableRow>
                  <TableRow>
                    <img
                      src={record.picture}
                      alt={record.contentName}
                      className="w-10 h-10 object-cover"
                    />
                  </TableRow>

                  <TableRow>
                    <span
                      className={`px-2 py-1 rounded ${
                        record.status === "active"
                          ? "text-green-500"
                          : "text-red-500"
                      }`}
                    >
                      {record.status}
                    </span>
                  </TableRow>

                  <TableRow>
                    <button
                      className="text-blue-500 px-2 py-1 rounded mr-2"
                      onClick={() =>
                        navigate("/vips/add-exclusive-deal", {
                          state: { id: record.id },
                        })
                      }
                    >
                      <FaEdit size={15} />
                    </button>
                    <button
                      className="text-red-500 px-2 py-1 rounded"
                      onClick={() => handleDelete(record.id)}
                    >
                      <FaTrash size={15} />
                    </button>
                  </TableRow>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        {totalPages > 1 && (
          <div className="flex text-sm justify-end items-center mt-2 px-2">
            Page
            {Array.from({ length: totalPages }, (_, index) => (
              <button
                key={index}
                onClick={() => setCurrentPage(index + 1)}
                className={`px-1 py-1 mx-1 ${
                  currentPage === index + 1 ? "text-blue-500" : "text-black/30"
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>
        )}
        {loading && (
          <div className="absolute inset-0 bg-gray-300/30 flex items-center justify-center backdrop-blur-sm">
            <SPINNER />
          </div>
        )}
      </section>
    </Container>
  );
};

export default ExclusiveList;
